import React, { useState, useEffect, useCallback, useRef } from 'react';
import VacationsService from '@services/VacationsService';
import { countWorkingDays } from '@lib/date';
import CorgeeStorageHelper from '@components/Globals/CorgeeStorageHelper';
import { useSocketConnectionId } from '@/contexts/SocketContext';
import { Modal } from '@/components/utils/Modal';
import './VacationsApprovalPage.css';

interface AssignedPerson {
  PersonID: number;
  Name: string;
  LeadId?: number;
}

interface Holiday {
  Date: string;
}

interface VacationRequestRaw {
  PersonId: number;
  LeadId: number;
  DateFrom: string;
  DateTo: string;
  CreatedAt: string;
  RequestTypeId: number;
  RequestTypeName: string;
  IsWorkingDay: boolean;
  IsHRApproverAllowed: boolean;
  IsPTOApproverAllowed: boolean;
  IsActive: boolean;
  Status: number;
  VacationRequestId: number;
  Comments: string;
  ApproverComments?: string;
  WorkedOn?: string;
}

interface TableRow {
  creationDate: string;
  leadName: string;
  requestedBy: AssignedPerson;
  requestTypeName: string;
  requestHRApproverAllowed: boolean;
  requestPTOApproverAllowed: boolean;
  days: number;
  dateFrom: string;
  dateTo: string;
  workedOn: string;
  status: string;
  requestId: number;
  requesterComments: string;
  approverComments: string;
  enabled: boolean;
}

type SortColumn =
  | 'default'
  | 'creationDate'
  | 'requestedBy'
  | 'requestType'
  | 'days'
  | 'from'
  | 'to'
  | 'workedOn'
  | 'requesterComments'
  | 'approverComments'
  | 'status';

type SortDirection = 'asc' | 'desc' | '';

const ALLOWED_CURRENT_DAY_PERMISSIONS = [
  'Absence',
  'Sick Leave',
  'Day Off',
  'Appointment',
  'Appointments',
  'Maternity Leave',
  'Bereavement Leave',
  'Paternity Leave',
];

function strDateToMMDDYYYY(str: string): string
{
  const [y, m, d] = str.split('-');
  return `${m}/${d}/${y}`;
}

function parseDate(str: string): Date
{
  const [y, m, d] = str.split('-').map(Number) as [number, number, number];
  return new Date(y, m - 1, d);
}

function mmddyyyyToDate(str: string): Date
{
  const [m, d, y] = str.split('/').map(Number) as [number, number, number];
  return new Date(y, m - 1, d);
}

function getRequestStatus(status: number): string
{
  if (status === 0) return 'Requested';
  if (status === 1) return 'Approved';
  return 'Rejected';
}

function getTimeZoneOffset(): string
{
  const offsetMinutes = new Date().getTimezoneOffset();
  const offsetHours = offsetMinutes / 60;
  const sign = offsetHours < 0 ? '+' : '-';
  const hours = Math.abs(Math.floor(offsetHours));
  return `${sign}${hours}`;
}

function checkEnabledDate(startDate: string): boolean
{
  const today = new Date().toISOString().split('T')[0]!;
  return mmddyyyyToDate(startDate).getTime() > parseDate(today).getTime();
}

function checkIsAllowedCurrentDateRequest(requestTypeName: string, startDate: string): boolean
{
  const today = new Date().toISOString().split('T')[0]!;
  return (
    ALLOWED_CURRENT_DAY_PERMISSIONS.includes(requestTypeName) &&
    mmddyyyyToDate(startDate).getTime() >= parseDate(today).getTime()
  );
}

function sortByMMDDYYYY(a: string, b: string, dir: SortDirection): number
{
  const dateA = mmddyyyyToDate(a);
  const dateB = mmddyyyyToDate(b);
  return dir === 'asc' ? dateA.getTime() - dateB.getTime() : dateB.getTime() - dateA.getTime();
}

interface SortIconProps {
  column: SortColumn;
  sortedBy: SortColumn;
  direction: SortDirection;
}

const SortIcon: React.FC<SortIconProps> = ({ column, sortedBy, direction }) => (
  <i
    className={
      sortedBy === column
        ? direction === 'asc'
          ? 'fa fa-sort-asc'
          : 'fa fa-sort-desc'
        : 'fa fa-sort'
    }
    style={{ display: 'inline-block', width: 20 }}
  />
);

interface SortableThProps {
  label: string;
  column: SortColumn;
  sortedBy: SortColumn;
  direction: SortDirection;
  onSort: (col: SortColumn) => void;
}

const SortableTh: React.FC<SortableThProps> = ({ label, column, sortedBy, direction, onSort }) => (
  <th className="enable-help-tooltip" onClick={() => onSort(column)}>
    <span className="headerIconPadding">{label}</span>
    <SortIcon column={column} sortedBy={sortedBy} direction={direction} />
  </th>
);

// ─── Main Component ────────────────────────────────────────────────────────────

const VacationsApproval: React.FC = () =>
{
  const personId: number = CorgeeStorageHelper.personId();
  const isAdmin: boolean = CorgeeStorageHelper.isAdmin();

  // ── State ──────────────────────────────────────────────────────────────────
  const [itemsAssigned, setItemsAssigned] = useState<AssignedPerson[]>([]);
  const [selectedAssigned, setSelectedAssigned] = useState<number[]>([]);
  // Latest holidays for async chains to avoid stale-closure reads
  const itemsHolidaysRef = useRef<Holiday[]>([]);
  const [itemsFillTable, setItemsFillTable] = useState<TableRow[]>([]);
  const [prevItemsFillTable, setPrevItemsFillTable] = useState<TableRow[]>([]);
  const [originalItems, setOriginalItems] = useState<TableRow[]>([]);
  const [isLoadingRequests, setIsLoadingRequests] = useState(true);
  const [_isMenuSidebarExpanded, setIsMenuSidebarExpanded] = useState(false);
  const [tableSortedBy, setTableSortedBy] = useState<SortColumn>('default');
  const [sortingDirection, setSortingDirection] = useState<SortDirection>('');
  const [showModal, setShowModal] = useState(false);
  const [selectedRequestItem, setSelectedRequestItem] = useState<TableRow | null>(null);
  const [approverCommentsValue, setApproverCommentsValue] = useState('');

  const tomorrow = (() =>
  {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    return d.toISOString().split('T')[0]!;
  })();

  const [selectedDateFrom] = useState(tomorrow);
  const [selectedDateTo] = useState(tomorrow);

  // ── Fill table ─────────────────────────────────────────────────────────────

  const fillTable = useCallback(
    (assigned: AssignedPerson[]) =>
    {
      setIsLoadingRequests(true);

      const vacationsModel = {
        PersonIds: personId.toString(),
        DateFrom: selectedDateFrom.slice(0, 10),
        DateTo: selectedDateTo.slice(0, 10),
        IsLead: true,
      };

      VacationsService.GetVacationRequestByIds(vacationsModel).then(
        (data: VacationRequestRaw[]) =>
        {
          const rows: TableRow[] = [];

          // Newest first
          [...data].reverse().forEach(item =>
          {
            const isFiltered =
              selectedAssigned.length === 0 || selectedAssigned.includes(item.PersonId);

            const requestedBy = assigned.find(a => a.PersonID === item.PersonId);
            if (!requestedBy || !isFiltered) return;

            const dateTo = new Date(item.DateTo.split('T')[0]!);
            const dateFrom = new Date(item.DateFrom.split('T')[0]!);
            let days = Math.round((dateTo.getTime() - dateFrom.getTime()) / 86_400_000) + 1;

            if (item.IsWorkingDay) days = countWorkingDays(dateFrom, dateTo, itemsHolidaysRef.current);

            const dateFromStr = strDateToMMDDYYYY(item.DateFrom.split('T')[0]!);
            const dateToStr = strDateToMMDDYYYY(item.DateTo.split('T')[0]!);
            const workedOn = item.WorkedOn ? strDateToMMDDYYYY(item.WorkedOn.split('T')[0]!) : '';
            const isCompleteRequest = workedOn !== dateFromStr;

            if (item.RequestTypeName === 'Day Off' && days === 0) days = 1;
            if (!isCompleteRequest) return;

            const isSwitch = item.RequestTypeId === 1;
            const status = item.IsActive ? getRequestStatus(item.Status) : 'Cancelled';

            const leadEntry = assigned.find(a => a.PersonID === item.LeadId);

            rows.push({
              creationDate: strDateToMMDDYYYY(item.CreatedAt.split('T')[0]!),
              leadName: leadEntry?.Name ?? '',
              requestedBy,
              requestTypeName: item.RequestTypeName,
              requestHRApproverAllowed: item.IsHRApproverAllowed ?? false,
              requestPTOApproverAllowed: item.IsPTOApproverAllowed ?? false,
              days,
              dateFrom: dateFromStr,
              dateTo: dateToStr,
              workedOn,
              status,
              requestId: item.VacationRequestId,
              requesterComments: item.Comments,
              approverComments: item.ApproverComments ?? '',
              enabled:
                isSwitch && status === 'Requested' && item.IsActive
                  ? true
                  : checkIsAllowedCurrentDateRequest(item.RequestTypeName, dateFromStr) &&
                      item.IsActive
                    ? true
                    : item.IsActive
                      ? checkEnabledDate(dateFromStr)
                      : false,
            });
          });

          setItemsFillTable(rows);
          setPrevItemsFillTable(rows);
          setOriginalItems(rows);
          setIsLoadingRequests(false);
        },
      );
    },

    [personId, selectedDateFrom, selectedDateTo, selectedAssigned],
  );

  // ── Data loading ───────────────────────────────────────────────────────────

  const getHolidays = useCallback(() =>
  {
    const sidebar = document.querySelector('.main-sidebar.expanded');
    setIsMenuSidebarExpanded(!!sidebar);

    return VacationsService.GetHolidays().then((data: Holiday[]) =>
    {
      itemsHolidaysRef.current = data;
      return data;
    });
  }, []);

  const getAllAssignedTo = useCallback(() =>
  {
    VacationsService.getAllAsignedToApproval().then(((data: AssignedPerson[]) =>
    {
      setItemsAssigned(data);
      fillTable(data);
    }) as any);
  }, [fillTable]);

  useEffect(() =>
  {
    document.title = 'Corgee';
  }, []);

  useEffect(() =>
  {
    // Load holidays before fillTable so working-day requests deduct them
    getHolidays().then(() => getAllAssignedTo());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Sorting ────────────────────────────────────────────────────────────────

  const sortByColumn = useCallback(
    (column: SortColumn, direction: SortDirection): TableRow[] =>
    {
      if (column === 'default') return originalItems;

      const base = [...prevItemsFillTable];
      const dir = direction;

      switch (column)
      {
        case 'creationDate':
          return base.sort((a, b) => sortByMMDDYYYY(a.creationDate, b.creationDate, dir));
        case 'requestedBy':
          return base.sort((a, b) =>
            dir === 'asc'
              ? a.requestedBy.Name.localeCompare(b.requestedBy.Name)
              : b.requestedBy.Name.localeCompare(a.requestedBy.Name),
          );
        case 'requestType':
          return base.sort((a, b) =>
            dir === 'asc'
              ? a.requestTypeName.localeCompare(b.requestTypeName)
              : b.requestTypeName.localeCompare(a.requestTypeName),
          );
        case 'days':
          return base.sort((a, b) => (dir === 'asc' ? a.days - b.days : b.days - a.days));
        case 'from':
          return base.sort((a, b) => sortByMMDDYYYY(a.dateFrom, b.dateFrom, dir));
        case 'to':
          return base.sort((a, b) => sortByMMDDYYYY(a.dateTo, b.dateTo, dir));
        case 'workedOn':
          return base.sort((a, b) =>
          {
            if (!a.workedOn) return 1;
            if (!b.workedOn) return -1;
            return sortByMMDDYYYY(a.workedOn, b.workedOn, dir);
          });
        case 'requesterComments':
          return base.sort((a, b) =>
            dir === 'asc'
              ? a.requesterComments.localeCompare(b.requesterComments)
              : b.requesterComments.localeCompare(a.requesterComments),
          );
        case 'approverComments':
          return base.sort((a, b) =>
            dir === 'asc'
              ? a.approverComments.localeCompare(b.approverComments)
              : b.approverComments.localeCompare(a.approverComments),
          );
        case 'status':
          return base.sort((a, b) =>
            dir === 'asc' ? a.status.localeCompare(b.status) : b.status.localeCompare(a.status),
          );
        default:
          return originalItems;
      }
    },
    [prevItemsFillTable, originalItems],
  );

  const sortTableBy = useCallback(
    (column: SortColumn) =>
    {
      if (!column || column === 'default')
      {
        setTableSortedBy('default');
        setSortingDirection('');
        setItemsFillTable(originalItems);
        return;
      }

      let nextDir: SortDirection;
      if (tableSortedBy === column && sortingDirection === 'asc')
      {
        nextDir = 'desc';
      }
      else if (tableSortedBy === column && sortingDirection === 'desc')
      {
        setTableSortedBy('default');
        setSortingDirection('');
        setItemsFillTable(originalItems);
        return;
      }
      else
      {
        nextDir = 'asc';
      }

      setTableSortedBy(column);
      setSortingDirection(nextDir);
      setItemsFillTable(sortByColumn(column, nextDir));
    },
    [tableSortedBy, sortingDirection, originalItems, sortByColumn],
  );

  // ── Actions ────────────────────────────────────────────────────────────────

  const showNotification = (message: string) =>
  {
    const el = document.createElement('div');
    el.className = 'clipboard-notification ng-scope';
    el.id = 'paste-image';
    el.style.cssText = 'display:none;z-index:99999;';
    el.innerHTML = `<div>${message}</div>`;
    document.body.prepend(el);
    // Simple fade in/out without jQuery
    el.style.display = 'block';
    setTimeout(() =>
    {
      el.style.opacity = '0';
      el.style.transition = 'opacity 2.4s';
      setTimeout(() => el.remove(), 2400);
    }, 6000);
  };

  const updateVacationRequest = useCallback(
    (selectedItem: TableRow, option: 'approve' | 'reject') =>
    {
      if (
        !isAdmin &&
        option === 'approve' &&
        !selectedItem.requestHRApproverAllowed &&
        !selectedItem.requestPTOApproverAllowed
      )
      {
        showNotification("Sorry, you don't have permissions to accept this request.");
        return;
      }

      const statusValue = option === 'approve' ? 1 : 2;
      const statusLabel = option === 'approve' ? 'approved' : 'rejected';

      const finalComments =
        approverCommentsValue.length > 0 && approverCommentsValue !== selectedItem.approverComments
          ? approverCommentsValue
          : (selectedItem.approverComments ?? '');

      const newModelRequest = {
        VacationRequestId: selectedItem.requestId,
        NotifyToId: selectedItem.requestedBy.PersonID,
        ApproverComments: finalComments,
        StatusValue: statusValue,
        TimeZone: getTimeZoneOffset(),
        SocketConnectionID: useSocketConnectionId,
      };

      VacationsService.UpdateVacationRequest(newModelRequest).then(() =>
      {
        showNotification(`The PTO Request has been ${statusLabel}.`);
        setShowModal(false);
        setSelectedRequestItem(null);
        setApproverCommentsValue('');
        fillTable(itemsAssigned);
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isAdmin, approverCommentsValue, useSocketConnectionId, itemsAssigned, fillTable],
  );

  const showConfirmRejectionModal = useCallback((item: TableRow) =>
  {
    setSelectedRequestItem(item);
    setApproverCommentsValue('');
    setShowModal(true);
  }, []);

  const closeModal = useCallback(() =>
  {
    setShowModal(false);
    setSelectedRequestItem(null);
    setApproverCommentsValue('');
  }, []);

  void useCallback((selected: AssignedPerson[]) =>
  {
    setSelectedAssigned(selected.map(p => p.PersonID));
  }, []);

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div id="VacationsApproval" className="relative overflow-y-auto max-h-screen">
      <div className="VacationsApproval" style={{ height: 'auto' }}>
        {/* Rejection modal */}
        <Modal open={showModal} onClose={closeModal} title="Reject PTO Request">
          <div className="md-dialog-content delete-item-modal">
            <div className="delete-text">Here you can write a rejection explanation.</div>
            <textarea
              name="comments"
              id="comments"
              cols={36}
              rows={2}
              maxLength={100}
              value={approverCommentsValue}
              onChange={e => setApproverCommentsValue(e.target.value)}
            />
          </div>

          <div className="pt-3 text-right gap-[10px] relative overflow-hidden min-h-6 flex flex-row-reverse">
            <button
              type="button"
              onClick={() =>
                selectedRequestItem && updateVacationRequest(selectedRequestItem, 'reject')
              }
              className="bg-corgee-blue hover:bg-corgee-BlueHover border text-white text-xs h-7 px-3 py-1"
            >
              Confirm
            </button>
            <button
              type="button"
              onClick={closeModal}
              className="bg-brightGray hover:bg-corgee-brightGrayHover border text-white text-xs h-7 px-3 py-1"
            >
              Cancel
            </button>
          </div>
        </Modal>

        {/* Header */}
        <div className="spaceRow">
          <div className="col-md-12">
            <div className="stime-header">
              <h3 className="text-[22px]">PTO Approval</h3>
              <p className="info_AR">
                This shows your employees' requests regarding their PTO. The user(s) should have
                their Hire Date up to date. Actions will be notified to the request owner and HR
                department.
              </p>
            </div>
          </div>
        </div>

        {/* Table */}
        <div>
          {/* Loading spinner */}
          <div
            className="text-center col-md-2 loadingContainerVacationsApproval"
            style={{
              visibility: isLoadingRequests ? 'visible' : 'hidden',
            }}
          >
            <div
              className="spinner-border"
              role="status"
              style={{ marginLeft: '-15rem', marginTop: '20%' }}
            >
              <span className="sr-only">Loading...</span>
            </div>
          </div>

          <div id="tableContainer" className="table-responsive wrapper table-wrapper">
            <table className="noTableBottom table table-sm table-bordered overflow-x-auto">
              <thead>
                <tr>
                  <SortableTh
                    label="Created On"
                    column="creationDate"
                    sortedBy={tableSortedBy}
                    direction={sortingDirection}
                    onSort={sortTableBy}
                  />
                  <SortableTh
                    label="Requested By"
                    column="requestedBy"
                    sortedBy={tableSortedBy}
                    direction={sortingDirection}
                    onSort={sortTableBy}
                  />
                  <SortableTh
                    label="Request Type"
                    column="requestType"
                    sortedBy={tableSortedBy}
                    direction={sortingDirection}
                    onSort={sortTableBy}
                  />
                  <SortableTh
                    label="Days"
                    column="days"
                    sortedBy={tableSortedBy}
                    direction={sortingDirection}
                    onSort={sortTableBy}
                  />
                  <SortableTh
                    label="From"
                    column="from"
                    sortedBy={tableSortedBy}
                    direction={sortingDirection}
                    onSort={sortTableBy}
                  />
                  <SortableTh
                    label="To"
                    column="to"
                    sortedBy={tableSortedBy}
                    direction={sortingDirection}
                    onSort={sortTableBy}
                  />
                  <SortableTh
                    label="Worked On"
                    column="workedOn"
                    sortedBy={tableSortedBy}
                    direction={sortingDirection}
                    onSort={sortTableBy}
                  />
                  <SortableTh
                    label="Requester Comments"
                    column="requesterComments"
                    sortedBy={tableSortedBy}
                    direction={sortingDirection}
                    onSort={sortTableBy}
                  />
                  <SortableTh
                    label="Approver Comments"
                    column="approverComments"
                    sortedBy={tableSortedBy}
                    direction={sortingDirection}
                    onSort={sortTableBy}
                  />
                  <SortableTh
                    label="Status"
                    column="status"
                    sortedBy={tableSortedBy}
                    direction={sortingDirection}
                    onSort={sortTableBy}
                  />
                  <th className="enable-help-tooltip">
                    <span className="headerIconPadding">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {!isLoadingRequests &&
                  (itemsFillTable.length > 0 ? (
                    itemsFillTable.map((row, idx) => (
                      <tr key={`${row.requestId}-${idx}`}>
                        <td className="stime-td-report">{row.creationDate}</td>
                        <td className="stime-td-report">{row.requestedBy.Name}</td>
                        <td className="stime-td-report">{row.requestTypeName}</td>
                        <td className="stime-td-report">{row.days}</td>
                        <td className="stime-td-report">{row.dateFrom}</td>
                        <td className="stime-td-report">{row.dateTo}</td>
                        <td className="stime-td-report">{row.workedOn}</td>
                        <td className="stime-td-report comments">{row.requesterComments}</td>
                        <td className="stime-td-report comments">{row.approverComments}</td>
                        <td className="stime-td-report">{row.status}</td>
                        <td className="stime-td-report">
                          {row.status !== 'Cancelled' ? (
                            <div>
                              {row.status !== 'Approved' && (
                                <button
                                  className="corgee-button confirm"
                                  onClick={() => updateVacationRequest(row, 'approve')}
                                >
                                  Accept
                                </button>
                              )}
                              {row.status !== 'Rejected' && (
                                <button
                                  className="corgee-button delete"
                                  onClick={() => showConfirmRejectionModal(row)}
                                >
                                  Reject
                                </button>
                              )}
                            </div>
                          ) : (
                            'Disabled'
                          )}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={11} style={{ border: 'none' }}>
                        <div className="row spaceRow" />
                        <div className="col-md-12">
                          <div className="stime-bottom-Results notFoundRecords">
                            No results found
                          </div>
                        </div>
                        <div className="row spaceRow" />
                        <div className="row spaceRow" />
                        <div className="row spaceRow" />
                        <div className="row spaceRow" />
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VacationsApproval;
